/**
 * Action Plan PDF Generator Service
 * Generates comprehensive, developer-ready technical implementation PDFs
 * for On-Page, Off-Page, Core Web Vitals, or the entire Action Plan.
 * Includes Marketing4Sight branding and client brand logo.
 */

import type { ActionPlanTask, ActionPlanTaskPage, TaskDetailsResponse } from './actionPlanService';
import { getTaskDetails } from './actionPlanService';
import m4sLogoUrl from '../assets/new-logo.png';

export interface GeneratePdfOptions {
  tasks: ActionPlanTask[];
  category: 'all' | 'onpage' | 'offpage' | 'technical';
  userId: string;
  websiteUrl?: string;
  brandLogoUrl?: string;
  businessName?: string;
  onProgress?: (msg: string) => void;
}

// Clean string for standard jsPDF fonts (removing non-printable characters or weird quotes)
function sanitize(str: string | number | undefined | null): string {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2026]/g, '...')
    .replace(/[^\x20-\x7E\t\n\r]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Load image in browser and convert to Base64 PNG Data URI with natural dimensions
 */
async function loadImageAsBase64(src?: string): Promise<{ data: string; width: number; height: number } | null> {
  if (!src) return null;
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const w = img.naturalWidth || img.width || 200;
          const h = img.naturalHeight || img.height || 100;
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            const data = canvas.toDataURL('image/png');
            resolve({ data, width: w, height: h });
          } else {
            resolve(null);
          }
        } catch (e) {
          console.warn('Canvas conversion error:', e);
          resolve(null);
        }
      };
      img.onerror = (err) => {
        console.warn('Image load error for:', src, err);
        resolve(null);
      };
      img.src = src;
    } catch {
      resolve(null);
    }
  });
}

export async function generateActionPlanPdf({
  tasks,
  category,
  userId,
  websiteUrl = '',
  brandLogoUrl,
  businessName = '',
  onProgress,
}: GeneratePdfOptions): Promise<void> {
  const { jsPDF } = await import('jspdf');

  onProgress?.('Preparing Action Plan data & logos...');

  // Filter tasks based on category
  const targetTasks = category === 'all' ? tasks : tasks.filter((t) => t.category === category);

  if (targetTasks.length === 0) {
    throw new Error('No tasks found to generate PDF.');
  }

  // Pre-load logos in parallel
  const [m4sLogo, brandLogo] = await Promise.all([
    loadImageAsBase64(m4sLogoUrl),
    loadImageAsBase64(brandLogoUrl),
  ]);

  // Pre-fetch live details for off-page and technical tasks that don't have pages populated
  const detailsMap: Record<number, TaskDetailsResponse | null> = {};
  const tasksNeedingDetails = targetTasks.filter(
    (t) => (t.category === 'offpage' || t.category === 'technical') && (!t.pages || t.pages.length === 0)
  );

  if (tasksNeedingDetails.length > 0 && userId) {
    onProgress?.(`Fetching live technical audit details (0/${tasksNeedingDetails.length})...`);
    let completedCount = 0;

    const CHUNK_SIZE = 4;
    for (let i = 0; i < tasksNeedingDetails.length; i += CHUNK_SIZE) {
      const chunk = tasksNeedingDetails.slice(i, i + CHUNK_SIZE);
      await Promise.all(
        chunk.map(async (t) => {
          try {
            const data = await getTaskDetails(userId, t.id);
            detailsMap[t.id] = data;
          } catch {
            detailsMap[t.id] = null;
          } finally {
            completedCount++;
            onProgress?.(`Fetching live technical audit details (${completedCount}/${tasksNeedingDetails.length})...`);
          }
        })
      );
    }
  }

  onProgress?.('Compiling technical PDF document...');

  // Document setup: A4 portrait (210 x 297 mm)
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth(); // 210
  const pageHeight = doc.internal.pageSize.getHeight(); // 297
  const margin = 14;
  const contentWidth = pageWidth - margin * 2; // 182mm
  const bottomMargin = 18;

  let y = margin;

  // Clean site URL and brand name
  const cleanSiteUrl = websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') || 'Target Website';
  const clientDisplayName = businessName || cleanSiteUrl;

  // Helper for page breaks - always restores standard drawing colors on new page
  const checkPageBreak = (neededHeight: number): boolean => {
    if (y + neededHeight > pageHeight - bottomMargin) {
      doc.addPage();
      y = margin + 6;
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240);
      doc.setTextColor(15, 23, 42);
      return true;
    }
    return false;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 1. COVER / HEADER SECTION WITH BOTH LOGOS
  // ─────────────────────────────────────────────────────────────────────────────

  // Top Accent Banner
  doc.setFillColor(37, 99, 235); // Royal Blue #2563eb
  doc.rect(0, 0, pageWidth, 5, 'F');

  y = margin + 2;

  // Top Logos Bar (Marketing4Sight Logo on Left, Brand Logo on Right)
  const logoBarHeight = 14;

  if (m4sLogo) {
    try {
      const maxW = 42;
      const maxH = 13;
      const aspect = m4sLogo.width / m4sLogo.height;
      let w = maxW;
      let h = maxW / aspect;
      if (h > maxH) {
        h = maxH;
        w = maxH * aspect;
      }
      doc.addImage(m4sLogo.data, 'PNG', margin, y, w, h);
    } catch {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(37, 99, 235);
      doc.text('MARKETING 4SIGHT', margin, y + 8);
    }
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(37, 99, 235);
    doc.text('MARKETING 4SIGHT', margin, y + 8);
  }

  // Right Side: Brand Logo or Brand Name Pill
  if (brandLogo) {
    try {
      const maxW = 38;
      const maxH = 13;
      const aspect = brandLogo.width / brandLogo.height;
      let w = maxW;
      let h = maxW / aspect;
      if (h > maxH) {
        h = maxH;
        w = maxH * aspect;
      }
      doc.addImage(brandLogo.data, 'PNG', pageWidth - margin - w, y, w, h);
    } catch {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text(sanitize(clientDisplayName), pageWidth - margin, y + 8, { align: 'right' });
    }
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(30, 41, 59);
    doc.text(sanitize(clientDisplayName), pageWidth - margin, y + 8, { align: 'right' });
  }

  y += logoBarHeight + 3;

  // Thin separator line
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, y, pageWidth - margin, y);
  y += 5;

  // Document Title Card
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, contentWidth, 34, 2, 2, 'FD');

  const titleCategoryLabel =
    category === 'all'
      ? 'Complete SEO Action Plan'
      : category === 'onpage'
      ? 'On-Page SEO Action Plan'
      : category === 'offpage'
      ? 'Off-Page & Authority Action Plan'
      : 'Core Web Vitals & Technical Action Plan';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(15, 23, 42); // Slate-900
  doc.text(titleCategoryLabel, margin + 5, y + 9);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(`Technical Implementation & Remediation Guide for ${sanitize(cleanSiteUrl)}`, margin + 5, y + 16);

  // Meta Badges inside Header Box
  const highCount = targetTasks.filter((t) => t.priority === 'high').length;
  const medCount = targetTasks.filter((t) => t.priority === 'medium').length;
  const completedCount = targetTasks.filter((t) => t.status === 'completed').length;
  const genDate = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  const statsLine = `Total Tasks: ${targetTasks.length}   |   High Priority: ${highCount}   |   Medium Priority: ${medCount}   |   Completed: ${completedCount}   |   Date: ${genDate}`;
  doc.text(statsLine, margin + 5, y + 25);

  y += 40;

  // Notice Banner for Technical Team
  doc.setFillColor(239, 246, 255); // Blue-50
  doc.setDrawColor(191, 219, 254); // Blue-200
  doc.roundedRect(margin, y, contentWidth, 14, 1.5, 1.5, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(29, 78, 216);
  doc.text('FOR TECHNICAL & ENGINEERING TEAMS:', margin + 4, y + 5.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(30, 58, 138);
  doc.text(
    'This document contains prioritized code, asset, and server-level optimizations identified by 4Sight SEO audits.',
    margin + 4,
    y + 10
  );

  y += 19;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. RENDER TASKS GROUPED BY CATEGORY
  // ─────────────────────────────────────────────────────────────────────────────

  const categoriesToRender: Array<{ id: 'onpage' | 'offpage' | 'technical'; label: string }> = [];
  if (category === 'all' || category === 'onpage') {
    categoriesToRender.push({ id: 'onpage', label: 'On-Page SEO Action Items' });
  }
  if (category === 'all' || category === 'offpage') {
    categoriesToRender.push({ id: 'offpage', label: 'Off-Page & Authority Action Items' });
  }
  if (category === 'all' || category === 'technical') {
    categoriesToRender.push({ id: 'technical', label: 'Core Web Vitals & Technical Speed Action Items' });
  }

  let globalTaskIndex = 1;

  for (const cat of categoriesToRender) {
    const catTasks = targetTasks.filter((t) => t.category === cat.id);
    if (catTasks.length === 0) continue;

    checkPageBreak(25);

    // Category Header Banner
    doc.setFillColor(30, 41, 59); // Slate-800
    doc.roundedRect(margin, y, contentWidth, 9, 1.5, 1.5, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text(`${cat.label.toUpperCase()} (${catTasks.length} Tasks)`, margin + 4, y + 6);

    y += 14;

    // Render Each Task
    for (const task of catTasks) {
      checkPageBreak(40);

      // Priority Colors
      const isHigh = task.priority === 'high';
      const isMedium = task.priority === 'medium';
      const priorityBg = isHigh ? [254, 242, 242] : isMedium ? [254, 252, 232] : [240, 253, 244];
      const priorityBorder = isHigh ? [254, 202, 202] : isMedium ? [254, 240, 138] : [187, 247, 208];
      const priorityText = isHigh ? [185, 28, 28] : isMedium ? [161, 98, 7] : [21, 128, 61];

      // Task Title Line
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.setTextColor(15, 23, 42);
      const titleStr = `${globalTaskIndex}. ${sanitize(task.title)}`;
      doc.text(titleStr, margin, y);

      // Priority Badge
      const priorityLabel = `${task.priority.toUpperCase()} PRIORITY`;
      doc.setFontSize(7.5);
      const badgeWidth = doc.getTextWidth(priorityLabel) + 6;
      const badgeX = pageWidth - margin - badgeWidth;

      doc.setFillColor(priorityBg[0], priorityBg[1], priorityBg[2]);
      doc.setDrawColor(priorityBorder[0], priorityBorder[1], priorityBorder[2]);
      doc.roundedRect(badgeX, y - 4, badgeWidth, 5.5, 1, 1, 'FD');

      doc.setTextColor(priorityText[0], priorityText[1], priorityText[2]);
      doc.text(priorityLabel, badgeX + 3, y - 0.3);

      y += 5.5;

      // Task Metadata Sub-line
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      const metaParts = [];
      if (task.parameter_group) metaParts.push(`Group: ${sanitize(task.parameter_group)}`);
      if (task.effort_description) metaParts.push(`Effort: ${task.effort_description}`);
      if (task.impact_score) metaParts.push(`Impact: ${task.impact_score}/100`);
      if (task.status) metaParts.push(`Status: ${task.status.replace('_', ' ').toUpperCase()}`);
      doc.text(metaParts.join('   |   '), margin, y);

      y += 5;

      // ── Impact / Why This Matters ──
      if (task.impact_description) {
        checkPageBreak(15);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(71, 85, 105);
        doc.text('Issue & Impact:', margin, y);
        y += 3.8;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(51, 65, 85);
        const impactLines = doc.splitTextToSize(sanitize(task.impact_description), contentWidth - 4);
        doc.text(impactLines, margin + 2, y);
        y += impactLines.length * 3.8 + 2;
      }

      // ── Technical Recommendation & Next Steps ──
      const recText = task.recommendation || task.description;
      if (recText) {
        const recLines = doc.splitTextToSize(sanitize(recText), contentWidth - 10);
        const boxHeight = recLines.length * 3.8 + 9;

        // Ensure page break check runs BEFORE setting fill colors
        checkPageBreak(boxHeight + 2);

        // Explicitly set soft light background & light gray border
        doc.setFillColor(241, 245, 249); // Slate-100 (light grey-blue)
        doc.setDrawColor(226, 232, 240); // Slate-200
        doc.roundedRect(margin, y, contentWidth, boxHeight, 1.5, 1.5, 'FD');

        // Explicitly set text colors inside the box
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(30, 41, 59); // Slate-800
        doc.text('Recommended Technical Fix:', margin + 3, y + 4.5);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(51, 65, 85); // Slate-700
        doc.text(recLines, margin + 3, y + 8.5);

        y += boxHeight + 4;
      }

      // ─────────────────────────────────────────────────────────────────────────
      // ── VIEW DETAILS / AFFECTED ASSETS TABLE FOR EVERY TASK
      // ─────────────────────────────────────────────────────────────────────────
      const pages = task.pages || [];
      const liveData = detailsMap[task.id];

      if (pages.length > 0) {
        // CASE A: Structured Task Pages (e.g. On-Page Assets, Images, URLs)
        checkPageBreak(30);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(15, 23, 42);
        doc.text(`Affected Assets & Specific URLs (${pages.length} identified):`, margin, y);
        y += 4;

        const colWidths = {
          asset: 56,
          issue: 38,
          current: 38,
          fix: 50,
        };

        const renderTableHeader = () => {
          doc.setFillColor(226, 232, 240);
          doc.setDrawColor(203, 213, 225);
          doc.rect(margin, y, contentWidth, 6, 'FD');

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7.2);
          doc.setTextColor(30, 41, 59);

          let curX = margin + 2;
          doc.text('ASSET / URL', curX, y + 4.2);
          curX += colWidths.asset;
          doc.text('ISSUE FOUND', curX, y + 4.2);
          curX += colWidths.issue;
          doc.text('CURRENT VALUE', curX, y + 4.2);
          curX += colWidths.current;
          doc.text('RECOMMENDED FIX', curX, y + 4.2);

          y += 6;
        };

        renderTableHeader();

        pages.forEach((p: ActionPlanTaskPage, pIdx: number) => {
          let assetLabel = sanitize(p.page_url);
          const isInline =
            assetLabel.startsWith('data:') ||
            assetLabel.startsWith('blob:') ||
            assetLabel.includes('svg%3E') ||
            (p.current_value && p.current_value.includes('svg%3E'));

          if (isInline) {
            assetLabel = `[Inline <svg> #${pIdx + 1}] Found on: ${cleanSiteUrl}`;
          }

          let curValClean = sanitize(p.current_value || '—');
          if (curValClean.includes('svg%3E')) {
            curValClean = curValClean.includes('loading=')
              ? 'loading="lazy" attribute missing on <svg>'
              : 'Missing alt attribute on <svg>';
          }

          const issueClean = sanitize(p.issue_description || '—');
          const fixClean = sanitize(p.recommended_value || '—');

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.2);

          const assetLines = doc.splitTextToSize(assetLabel, colWidths.asset - 3);
          const issueLines = doc.splitTextToSize(issueClean, colWidths.issue - 3);
          const curValLines = doc.splitTextToSize(curValClean, colWidths.current - 3);
          const fixLines = doc.splitTextToSize(fixClean, colWidths.fix - 3);

          const maxLines = Math.max(assetLines.length, issueLines.length, curValLines.length, fixLines.length, 1);
          const rowHeight = maxLines * 3.3 + 4;

          if (checkPageBreak(rowHeight + 6)) {
            renderTableHeader();
          }

          if (pIdx % 2 === 1) {
            doc.setFillColor(248, 250, 252);
            doc.rect(margin, y, contentWidth, rowHeight, 'F');
          }

          doc.setDrawColor(241, 245, 249);
          doc.line(margin, y + rowHeight, margin + contentWidth, y + rowHeight);

          let curX = margin + 2;
          doc.setTextColor(37, 99, 235);
          doc.text(assetLines, curX, y + 3.2);

          curX += colWidths.asset;
          doc.setTextColor(220, 38, 38);
          doc.text(issueLines, curX, y + 3.2);

          curX += colWidths.issue;
          doc.setTextColor(71, 85, 105);
          doc.text(curValLines, curX, y + 3.2);

          curX += colWidths.current;
          doc.setTextColor(22, 101, 52);
          doc.text(fixLines, curX, y + 3.2);

          y += rowHeight;
        });

        if (pages.length > 15) {
          y += 2;
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(7);
          doc.setTextColor(148, 163, 184);
          doc.text(`... and ${pages.length - 15} additional affected assets/pages.`, margin + 2, y);
          y += 3;
        }

        y += 4;
      } else if (liveData && liveData.detail_type === 'table' && liveData.rows && liveData.rows.length > 0) {
        // CASE B: Live Technical Audit Table (e.g. DataForSEO DA, PSI CWV)
        checkPageBreak(25);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(15, 23, 42);
        doc.text(`Live Technical Audit Data (${sanitize(liveData.title)}):`, margin, y);
        y += 4;

        if (liveData.summary && Object.keys(liveData.summary).length > 0) {
          const summaryStr = Object.entries(liveData.summary)
            .map(([k, v]) => `${sanitize(k)}: ${sanitize(v)}`)
            .join('   |   ');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.5);
          doc.setTextColor(71, 85, 105);
          doc.text(summaryStr, margin, y);
          y += 4;
        }

        const cols = liveData.columns || Object.keys(liveData.rows[0]);
        const colW = contentWidth / cols.length;

        const renderLiveHeader = () => {
          doc.setFillColor(226, 232, 240);
          doc.setDrawColor(203, 213, 225);
          doc.rect(margin, y, contentWidth, 6, 'FD');

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7.2);
          doc.setTextColor(30, 41, 59);

          cols.forEach((col, cIdx) => {
            doc.text(sanitize(col).toUpperCase(), margin + cIdx * colW + 2, y + 4.2);
          });
          y += 6;
        };

        renderLiveHeader();

        liveData.rows.slice(0, 12).forEach((row, rIdx) => {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.2);

          const cellLinesList = cols.map((col) => {
            const val = sanitize(row[col] ?? '—');
            return doc.splitTextToSize(val, colW - 3);
          });

          const maxL = Math.max(...cellLinesList.map((l) => l.length), 1);
          const rowH = maxL * 3.3 + 3.5;

          if (checkPageBreak(rowH + 6)) {
            renderLiveHeader();
          }

          if (rIdx % 2 === 1) {
            doc.setFillColor(248, 250, 252);
            doc.rect(margin, y, contentWidth, rowH, 'F');
          }

          doc.setDrawColor(241, 245, 249);
          doc.line(margin, y + rowH, margin + contentWidth, y + rowH);

          cols.forEach((col, cIdx) => {
            const isColStatus = col.toLowerCase().includes('status');
            const isColAction = col.toLowerCase() === 'action';
            if (isColStatus) {
              doc.setTextColor(180, 83, 9);
            } else if (isColAction) {
              doc.setTextColor(22, 101, 52);
            } else {
              doc.setTextColor(51, 65, 85);
            }
            doc.text(cellLinesList[cIdx], margin + cIdx * colW + 2, y + 3.2);
          });

          y += rowH;
        });

        y += 4;
      } else {
        // CASE C: Structured Implementation & Technical Scope Table
        // Guarantees every On-Page, Off-Page, and Core Web Vitals task has a structured View Details table
        checkPageBreak(28);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(15, 23, 42);
        doc.text('Technical Implementation & Scope Breakdown:', margin, y);
        y += 4;

        const renderGenericHeader = () => {
          doc.setFillColor(226, 232, 240);
          doc.setDrawColor(203, 213, 225);
          doc.rect(margin, y, contentWidth, 6, 'FD');

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7.2);
          doc.setTextColor(30, 41, 59);

          doc.text('TECHNICAL PARAMETER', margin + 2, y + 4.2);
          doc.text('DETECTED STATE', margin + 50, y + 4.2);
          doc.text('ENGINEERING DIRECTIVE', margin + 105, y + 4.2);

          y += 6;
        };

        renderGenericHeader();

        const paramLabel = sanitize(task.sub_parameter || task.parameter_group || task.title);
        const detectedState = sanitize(task.impact_description || 'Needs Technical Remediation');
        const directive = sanitize(
          liveData && liveData.detail_type === 'action_item' && liveData.text
            ? liveData.text
            : task.recommendation || task.description || 'Implement code-level fixes to meet Google standards.'
        );

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.2);

        const colW1 = 48;
        const colW2 = 55;
        const colW3 = 79;

        const pLines = doc.splitTextToSize(paramLabel, colW1 - 3);
        const dLines = doc.splitTextToSize(detectedState, colW2 - 3);
        const dirLines = doc.splitTextToSize(directive, colW3 - 3);

        const maxL = Math.max(pLines.length, dLines.length, dirLines.length, 1);
        const rowH = maxL * 3.3 + 4;

        if (checkPageBreak(rowH + 6)) {
          renderGenericHeader();
        }

        doc.setFillColor(248, 250, 252);
        doc.rect(margin, y, contentWidth, rowH, 'F');

        doc.setDrawColor(241, 245, 249);
        doc.line(margin, y + rowH, margin + contentWidth, y + rowH);

        doc.setTextColor(30, 41, 59);
        doc.text(pLines, margin + 2, y + 3.2);

        doc.setTextColor(185, 28, 28);
        doc.text(dLines, margin + 50, y + 3.2);

        doc.setTextColor(22, 101, 52);
        doc.text(dirLines, margin + 105, y + 3.2);

        y += rowH + 4;
      }

      // Divider line between tasks
      y += 2;
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, y, margin + contentWidth, y);
      y += 6;

      globalTaskIndex++;
    }

    y += 6;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. RUNNING FOOTER ON ALL PAGES
  // ─────────────────────────────────────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(148, 163, 184);

    // Footer line
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

    // Left: Platform note
    doc.text(`Marketing 4Sight  •  Confidential Technical Implementation Guide  •  ${sanitize(cleanSiteUrl)}`, margin, pageHeight - 7);

    // Right: Page number
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 7, { align: 'right' });
  }

  // Generate clean filename and trigger download
  const dateStr = new Date().toISOString().split('T')[0];
  const domainClean = cleanSiteUrl.replace(/[^a-zA-Z0-9]/g, '_');
  const catPrefix = category === 'all' ? 'Full_Action_Plan' : `Action_Plan_${category.toUpperCase()}`;
  const filename = `4Sight_${catPrefix}_${domainClean}_${dateStr}.pdf`;

  onProgress?.('Downloading PDF...');
  doc.save(filename);
}
